import React, {useCallback, useMemo, useState} from "react";
import {toast} from "react-toastify";
import RemoveGameMutation from "./mutation/RemoveGame";
import {formattedDateTime} from "../../util/date";
import InitialsAvatar from "../../util/name_initial";
import {confirmDelete} from "../common/confirmDelete";
import {GlobalStyles} from "../../constants/styles";
import GameSanctionModal from "./GameSanctionModal";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import SoccerGameDetailsModal from "./soccer/SoccerGameDetailsModal";
import HockeyGameDetailsModal from "./hockey/HockeyGameDetailsModal";
import BaseballInningScoreModal from "./BaseballInningScoreModal";
import useHasFeature from "../common/hooks/useHasFeature";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const GAME_END_STATUSES = new Set([
    "COMPLETED",
    "CANCELED",
    "FORFEIT",
    "DOUBLE_FORFEIT",
    "EXTRA",
]);

export const getInningScoreStatus = (status) => {
    const normalizedStatus = String(status || "").toUpperCase();
    return GAME_END_STATUSES.has(normalizedStatus) ? normalizedStatus : "IN_PROGRESS";
};

export const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
        case "in_progress":
            return "danger";
        case "completed":
            return "success";
        case "forfeit":
        case "double_forfeit":
            return "warning";
        default:
            return "secondary";
    }
};

export default function GameElement({
                                        event,
                                        sportKey: sportKeyProp,
                                        handleRemoveGame,
                                        updateScore,
                                        stages,
                                        games,
                                        referees,
                                        hideActions = false,
                                        canEdit = true,
                                        selected = false,
                                        onToggleSelect,
                                        assignmentStatus = null,
                                    }) {
    const {t, language} = useTranslation();
    const colors = useThemeColors();
    const [removeGameMutation] = RemoveGameMutation();
    const game = event?.game;
    const [sanctionOpen, setSanctionOpen] = useState(false);
    const [soccerDetailsOpen, setSoccerDetailsOpen] = useState(false);
    const [hockeyDetailsOpen, setHockeyDetailsOpen] = useState(false);
    const [inningScoreOpen, setInningScoreOpen] = useState(false);

    const {hasFeature} = useHasFeature();
    const canOpenDetailedResults = hasFeature("pro_detailed_results");
    const sportKey = String(sportKeyProp || event?.season?.division?.sport?.key || "").toLowerCase();
    const isInningSport = sportKey === "baseball" || sportKey === "softball";

    const openSoccerDetails = useCallback(() => {
        if (!canOpenDetailedResults) {
            toast.warning(t("gameElement.soccerGameSheet.proDetailedResultsRequired"), {
                autoClose: 5000,
                toastId: "soccer_game_sheet_pro_detailed_results_required",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }
        setSoccerDetailsOpen(true);
    }, [canOpenDetailedResults, t]);
    const openInningScores = useCallback(() => {
        if (!canOpenDetailedResults) {
            toast.warning(t("gameElement.soccerGameSheet.proDetailedResultsRequired"), {
                autoClose: 5000,
                toastId: "inning_score_detailed_results_required",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }
        setInningScoreOpen(true);
    }, [canOpenDetailedResults, t]);
    const closeSoccerDetails = useCallback(() => setSoccerDetailsOpen(false), []);
    const openHockeyDetails = useCallback(() => {
        if (!canOpenDetailedResults) {
            toast.warning(t("gameElement.soccerGameSheet.proDetailedResultsRequired"), {
                autoClose: 5000,
                toastId: "hockey_game_sheet_pro_detailed_results_required",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }
        setHockeyDetailsOpen(true);
    }, [canOpenDetailedResults, t]);
    const closeHockeyDetails = useCallback(() => setHockeyDetailsOpen(false), []);

    const openSanction = useCallback(() => setSanctionOpen(true), []);
    const closeSanction = useCallback(() => setSanctionOpen(false), []);

    const scopeToken = useMemo(() => {
        const raw = String(event?.id ?? "x");
        return `kbola_game_${raw.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
    }, [event?.id]);

    const scopeSel = `tr[data-kbola-game="${scopeToken}"]`;

    const homeResting = game?.noPlay === true && !game?.homeTeam?.name;
    const visitorResting = game?.noPlay === true && !game?.visitorTeam?.name;

    const removeGame = async (gameId) => {
        try {
            const res = await removeGameMutation({
                variables: {gameId: parseInt(gameId, 10)},
            });

            if (res.data?.deleteGame?.success) {
                handleRemoveGame(gameId);

                toast.success(t("gameElement.messages.gameRemovedSuccessfully"), {
                    autoClose: 3000,
                    toastId: "remove_game_success",
                    position: "top-center",
                    closeOnClick: true,
                });

                window.location.reload();
            } else {
                toast.error(t("gameElement.messages.failedToRemoveGame"), {
                    autoClose: 3000,
                    toastId: "remove_game_error_generic",
                    position: "top-center",
                    closeOnClick: true,
                });
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                autoClose: 3000,
                toastId: "remove_game_error",
                position: "top-center",
                closeOnClick: true,
            });
        }
    };

    const requestRemoveGame = (g) => {
        const visitorName = g?.visitorTeam?.name || g?.visitorTeamAlias || "Visitor";
        const homeName = g?.homeTeam?.name || g?.homeTeamAlias || "Home";

        confirmDelete({
            title: t("gameElement.confirmDelete.title"),
            message: t("gameElement.confirmDelete.message", {
                visitorName,
                homeName,
            }),
            confirmLabel: t("gameElement.confirmDelete.confirmLabel"),
            cancelLabel: t("gameElement.confirmDelete.cancelLabel"),
            danger: true,
            onConfirm: async () => {
                await removeGame(g.id);
            },
        });
    };

    const stageGroupLabel = [game?.event?.stage?.name, game?.group?.name]
        .filter(Boolean)
        .join(" / ");

    const showScore = event?.status && event.status !== "SCHEDULED";

    const whenText =
        visitorResting || homeResting
            ? (event?.date || "").split("T")[0]
            : formattedDateTime(event?.date, event?.timezone, language);

    const locationText = visitorResting || homeResting ? "" : event?.field?.name || "";
    const refsText =
        visitorResting || homeResting
            ? ""
            : (event?.responsibles || []).map((res) => res.fullName).join(", ");

    const managersText =
        visitorResting || homeResting
            ? ""
            : (event?.managers || []).map((res) => res.fullName).join(", ");

    const TeamCell = ({team, alias, logoAlt}) => {
        const name = team?.name || alias || "";
        const logo = team?.logoUrl;

        return (
            <div className="kbola-teamCell">
                {logo ? (
                    <img
                        className="kbola-teamLogo"
                        width="46"
                        height="46"
                        src={logo}
                        alt={logoAlt}
                    />
                ) : (
                    <div className="kbola-avatarWrap">
                        <InitialsAvatar
                            name={name}
                            size={46}
                            backgroundColor={colors.primary}
                            textColor={colors.textOnPrimary}
                        />
                    </div>
                )}

                <div className="kbola-teamText" title={name}>
                    {name}
                </div>
            </div>
        );
    };

    if (!game) return null;

    const coachLabel =
        event?.stage?.scheduleType === "TRAINING"
            ? t("gameElement.coachLabel", {value: refsText || t("gameElement.emptyValue")})
            : t("gameElement.refereeUmpireLabel", {value: refsText || t("gameElement.emptyValue")});

    const managerLabel = t("gameElement.managerLabel", {value: managersText || t("gameElement.emptyValue")});

    return (
        <>
            <tr data-kbola-game={scopeToken} className="kbola-gameRow">

                <td className="align-middle text-center">
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={onToggleSelect}
                        disabled={event.status !== "SCHEDULED" || hideActions}
                    />
                </td>

                <td className="align-middle">
                    <span className="kbola-pill kbola-pillInfo">
                        {t("gameElement.matchweekLabel", {
                            matchDay: game?.matchDay ?? "",
                        })}
                    </span>
                </td>

                <td className="align-middle">
                    <span className="kbola-pill kbola-pillBrand">
                        {stageGroupLabel || t("gameElement.emptyValue")}
                    </span>
                </td>

                <td className="align-middle">
                    {visitorResting ? (
                        <span className="kbola-pill kbola-pillWarn">
                            {t("gameElement.noGame")}
                        </span>
                    ) : (
                        <TeamCell
                            team={game?.visitorTeam}
                            alias={game?.visitorTeamAlias}
                            logoAlt={t("gameElement.visitorLogoAlt")}
                        />
                    )}
                </td>

                <td className="align-middle text-center">
                    <span className="kbola-at">@</span>
                </td>

                <td className="align-middle">
                    {homeResting ? (
                        <span className="kbola-pill kbola-pillWarn">
                            {t("gameElement.noGame")}
                        </span>
                    ) : (
                        <TeamCell
                            team={game?.homeTeam}
                            alias={game?.homeTeamAlias}
                            logoAlt={t("gameElement.homeLogoAlt")}
                        />
                    )}
                </td>

                <td className="align-middle">
                    {showScore ? (
                        <span
                            className={`kbola-pill kbola-pillStatus kbola-pill-${getStatusStyle(
                                event.status
                            )}`}
                        >
                            <span className="kbola-scoreResult">
                                <span className="kbola-scoreValue">
                                    {game?.scoreVisitor ?? 0} - {game?.scoreHome ?? 0}
                                </span>
                                {sportKey === "soccer" &&
                                game?.penaltiesHome != null &&
                                game?.penaltiesVisitor != null ? (
                                    <span
                                        className="kbola-penaltyBadge"
                                        title={t("gameElement.penaltiesShort")}
                                    >
                                        <i className="fas fa-bolt" aria-hidden="true"/>
                                        {game.penaltiesVisitor}-{game.penaltiesHome} {t("gameElement.penaltiesShort")}
                                    </span>
                                ) : null}
                            </span>
                            <span className="kbola-scoreStatus">
                                {t(`home.eventStatus.${event.status.toLowerCase()}`)}
                            </span>
                        </span>
                    ) : (
                        <span className="kbola-pill kbola-pillMuted">
                            {t("gameElement.scheduled")}
                        </span>
                    )}
                </td>

                <td className="align-middle">
                    <div className="kbola-meta">
                        <span className="kbola-pill kbola-trn-pillMuted">{whenText}</span>
                        <span className="kbola-pill kbola-trn-pillMuted">
                            {t("gameElement.locationLabel", {
                                value: locationText || t("gameElement.emptyValue"),
                            })}
                        </span>
                        <div className="kbola-refereeMeta">
                            <span className={`kbola-pill ${assignmentStatus ? `kbola-assignment-pill kbola-assignment-${assignmentStatus}` : "kbola-trn-pillMuted"}`}>
                                {assignmentStatus && (
                                    <i
                                        className={`fas ${{
                                            unassigned: "fa-user-plus",
                                            manual: "fa-user-edit",
                                            proposed: "fa-paper-plane",
                                            invited: "fa-envelope",
                                            accepted: "fa-check-circle",
                                            declined: "fa-times-circle",
                                            completed: "fa-flag-checkered",
                                        }[assignmentStatus] || "fa-user-clock"} mr-1`}
                                        aria-hidden="true"
                                    />
                                )}
                                {coachLabel}
                                {assignmentStatus && ` · ${t(`fixtures.assignmentFilter.${assignmentStatus}`)}`}
                            </span>
                        </div>
                        <span className="kbola-pill kbola-trn-pillMuted">{managerLabel}</span>
                    </div>
                </td>


                <td className="project-actions text-right align-middle">
                    <div className="margin" key={game?.id}>
                        <div className="btn-group kbola-actionsGroup">
                            <button
                                type="button"
                                className="btn btn-kbola btn-sm kbola-actionsBtn"
                            >
                                {t("gameElement.actions")}
                            </button>

                            <button
                                type="button"
                                className="btn btn-outline-kbola btn-sm dropdown-toggle dropdown-icon kbola-actionsToggle"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                title={t("gameElement.more")}
                            >
                                <span className="sr-only">
                                    {t("gameElement.toggleDropdown")}
                                </span>
                            </button>

                            <div
                                className="dropdown-menu dropdown-menu-right kbola-actionsMenu"
                                role="menu"
                            >
                                {!hideActions && <a
                                    className="dropdown-item kbola-actionsItem"
                                    data-toggle="modal"
                                    data-target={`#game-result-modal${game?.id}`}
                                >
                                    <i className="fas fa-clipboard-check mr-2"/>
                                    {t("gameElement.enterResult")}
                                </a>}

                                {isInningSport && <button
                                    type="button"
                                    className="dropdown-item kbola-actionsItem"
                                    onClick={openInningScores}
                                >
                                    <i className="fas fa-bars mr-2"/>
                                    {t("gameElement.enterInnings")}
                                </button>}

                                {sportKey === "soccer" && <button
                                    type="button"
                                    className="dropdown-item kbola-actionsItem"
                                    onClick={openSoccerDetails}
                                >
                                    <i className="fas fa-bars mr-2"/>
                                    {t("gameElement.enterDetails")}
                                </button>}

                                {sportKey === "hockey" && <button
                                    type="button"
                                    className="dropdown-item kbola-actionsItem"
                                    onClick={openHockeyDetails}
                                >
                                    <i className="fas fa-hockey-puck mr-2"/>
                                    {t("gameElement.enterDetails")}
                                </button>}

                                {event.status === "SCHEDULED" && !hideActions && (
                                    <a
                                        className="dropdown-item kbola-actionsItem"
                                        data-toggle="modal"
                                        data-target={`#edit-game-modal${game?.id}`}
                                    >
                                        <i className="fas fa-edit mr-2"/>
                                        {t("gameElement.editGame")}
                                    </a>
                                )}

                                {!hideActions && <button
                                    type="button"
                                    className="dropdown-item kbola-actionsItem"
                                    onClick={openSanction}
                                >
                                    <i className="fas fa-gavel mr-2"/>
                                    {t("gameElement.addSanction")}
                                </button>}

                                {event.status === "SCHEDULED" && !hideActions && (
                                    <>
                                        <div className="dropdown-divider"/>
                                        <button
                                            type="button"
                                            className="dropdown-item kbola-actionsItem kbola-dangerItem"
                                            onClick={() => requestRemoveGame(game)}
                                        >
                                            <i className="fas fa-trash mr-2"/>
                                            {t("gameElement.deleteGame")}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>


                </td>
                <style>{`
          ${scopeSel} { font-family: 'Poppins', sans-serif; }

          ${scopeSel} i.fas,
          ${scopeSel} i.far,
          ${scopeSel} i.fal,
          ${scopeSel} i.fa,
          ${scopeSel} i[class^="fa-"],
          ${scopeSel} i[class*=" fa-"] {
            font-family: "Font Awesome 5 Free" !important;
            font-weight: 900;
          }
          ${scopeSel} i.fab {
            font-family: "Font Awesome 5 Brands" !important;
            font-weight: 400;
          }

          ${scopeSel}.kbola-gameRow:hover td {
            background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.08);
          }

          ${scopeSel} .kbola-pill {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 10px;
            border-radius: 999px;
            font-weight: 900;
            font-size: 12px;
            border: 1px solid rgba(0,0,0,0.10);
            line-height: 1;
            white-space: nowrap;
          }
          ${scopeSel} .kbola-pillStatus {
            gap: 8px;
          }
          ${scopeSel} .kbola-scoreResult {
            display: inline-flex;
            align-items: center;
            gap: 7px;
          }
          ${scopeSel} .kbola-scoreValue {
            letter-spacing: 0.03em;
          }
          ${scopeSel} .kbola-penaltyBadge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 4px 7px;
            border-radius: 7px;
            background: rgba(20,21,26,0.08);
            border: 1px solid rgba(20,21,26,0.12);
            color: rgba(20,21,26,0.72);
            font-size: 10px;
            font-weight: 900;
            letter-spacing: 0.02em;
          }
          ${scopeSel} .kbola-penaltyBadge i {
            color: rgba(120,83,0,0.95);
            font-size: 9px;
          }
          ${scopeSel} .kbola-scoreStatus {
            color: inherit;
            opacity: 0.78;
            font-size: 11px;
            font-weight: 800;
          }
          ${scopeSel} .kbola-pillInfo {
            background: rgba(13,110,253,0.10);
            color: rgba(13,110,253,0.95);
            border-color: rgba(13,110,253,0.18);
          }
          ${scopeSel} .kbola-pillBrand {
            background: ${colors.secondary};
            color: ${colors.textOnSecondary};
            border-color: rgba(0,0,0,0.10);
          }
          ${scopeSel} .kbola-pillWarn {
            background: rgba(255,193,7,0.14);
            color: rgba(133,100,4,1);
            border-color: rgba(255,193,7,0.22);
          }
          ${scopeSel} .kbola-pillMuted {
            background: rgba(0,0,0,0.03);
            color: rgba(20,21,26,0.72);
            border-color: rgba(0,0,0,0.08);
            font-weight: 800;
          }
          ${scopeSel} .kbola-pill-danger {
            background: rgba(220,53,69,0.10);
            color: rgba(220,53,69,0.95);
            border-color: rgba(220,53,69,0.18);
          }
          ${scopeSel} .kbola-pill-success {
            background: rgba(25,135,84,0.10);
            color: rgba(25,135,84,0.95);
            border-color: rgba(25,135,84,0.18);
          }
          ${scopeSel} .kbola-pill-warning {
            background: rgba(255,193,7,0.12);
            color: rgba(120,83,0,0.95);
            border-color: rgba(255,193,7,0.28);
          }
          ${scopeSel} .kbola-trn-pillSuccess {
            background: ${GlobalStyles.colors.lightSuccess} !important;
            color: ${GlobalStyles.colors.success} !important;
            border-color: ${GlobalStyles.colors.success} !important;
          }
          ${scopeSel} .kbola-pill-secondary {
            background: rgba(108,117,125,0.10);
            color: rgba(108,117,125,0.95);
            border-color: rgba(108,117,125,0.18);
          }

          ${scopeSel} .kbola-teamCell {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 0;
            min-width: 0;
          }
          ${scopeSel} .kbola-teamLogo {
            border-radius: 12px;
            padding: 4px;
            border: 1px solid rgba(0,0,0,0.06);
            object-fit: contain;
          }
          ${scopeSel} .kbola-avatarWrap {
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid rgba(0,0,0,0.06);
            background: ${colors.primary};
          }
          ${scopeSel} .kbola-teamText {
            font-size: 14px;
            font-weight: 900;
            color: #14151a;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 240px;
          }

          ${scopeSel} .kbola-trn-pillMuted {
            background: rgba(0,0,0,0.03) !important;
            color: rgba(20,21,26,0.72) !important;
            border-color: rgba(0,0,0,0.08) !important;
            font-weight: 800 !important;
          }
          ${scopeSel} .kbola-at { font-weight: 900; color: rgba(20,21,26,0.55); }

          ${scopeSel} .kbola-meta {
            display: flex;
            flex-direction: column;
            gap: 6px;
            align-items: flex-start;
          }

          ${scopeSel} .kbola-refereeMeta {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 6px;
            max-width: 100%;
          }

          ${scopeSel} .project-actions,
          ${scopeSel} .btn-group,
          ${scopeSel} .margin {
            overflow: visible !important;
          }

          ${scopeSel} .kbola-actionsBtn,
          ${scopeSel} .kbola-actionsToggle {
            border-radius: 12px;
            font-weight: 900;
          }
          ${scopeSel} .kbola-actionsBtn {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }
          ${scopeSel} .kbola-actionsToggle {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            width: 42px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding-left: 0;
            padding-right: 0;
          }

          ${scopeSel} .kbola-actionsMenu {
            border-radius: 14px;
            border: 1px solid rgba(0,0,0,0.10);
            box-shadow: 0 18px 44px rgba(0,0,0,0.12);
            padding: 6px;
            min-width: 220px;
          }
          ${scopeSel} .kbola-actionsItem {
            border-radius: 12px;
            font-weight: 850;
            padding: 10px 10px;
            display: flex;
            align-items: center;
          }
          ${scopeSel} .kbola-actionsItem:hover {
            background: ${colors.primary};
          }
          ${scopeSel} .kbola-dangerItem {
            color: #dc3545;
          }
          ${scopeSel} .kbola-dangerItem:hover {
            background: ${colors.primary};
          }

          ${scopeSel} button:focus,
          ${scopeSel} .btn:focus,
          ${scopeSel} .dropdown-item:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          }

          @media (max-width: 576px) {
            ${scopeSel} .kbola-teamText { max-width: 140px; }
          }
        `}</style>
            </tr>

            <GameSanctionModal
                open={sanctionOpen}
                onClose={closeSanction}
                season={event?.season}
                gameEvent={event}
                onCreated={async () => {
                }}
            />

            <SoccerGameDetailsModal
                open={soccerDetailsOpen}
                onClose={closeSoccerDetails}
                gameId={game?.id}
                teamId={game?.homeTeam?.id}
                homeTeam={game?.homeTeam}
                visitorTeam={game?.visitorTeam}
                scoreHome={game?.scoreHome}
                scoreVisitor={game?.scoreVisitor}
                penaltiesHome={game?.penaltiesHome}
                penaltiesVisitor={game?.penaltiesVisitor}
                canEdit={canEdit}
            />
            <HockeyGameDetailsModal
                open={hockeyDetailsOpen}
                onClose={closeHockeyDetails}
                gameId={game?.id}
                homeTeam={game?.homeTeam}
                visitorTeam={game?.visitorTeam}
                scoreHome={game?.scoreHome}
                scoreVisitor={game?.scoreVisitor}
                canEdit={canEdit}
                onScoreChange={(scoreHome, scoreVisitor) => updateScore?.(
                    game.id,
                    scoreHome,
                    scoreVisitor,
                    event.status,
                    game.forfeitTeam,
                    null,
                    game.penaltiesHome,
                    game.penaltiesVisitor
                )}
            />
            <BaseballInningScoreModal
                open={inningScoreOpen}
                onClose={() => setInningScoreOpen(false)}
                game={game}
                event={event}
                onSaved={(scoreVisitor, scoreHome, inningScores, status) => updateScore?.(
                    game.id,
                    scoreHome,
                    scoreVisitor,
                    String(status || getInningScoreStatus(event.status)).toUpperCase(),
                    game.forfeitTeam,
                    inningScores
                )}
            />
        </>
    );
}
